import { SxProps, Theme as MuiTheme } from '@mui/material/styles';
import theme from '../../theme';
import { HEADER_CONTROL_OUTER_RADIUS, HEADER_CONTROL_PADDING, HEADER_CONTROL_RADIUS } from '../UI/headerControl';

const HOVER_GROUND = '#EDF1F4';
const PRESSED_GROUND = '#E2E8EC';
const ACCENT = theme.palette?.myPrimary?.lightBlue100 ?? '#2392BA';
const SELECTED_GROUND = theme.palette?.myPrimary?.lightGrey ?? '#F0F9FC';
const SWATCH_BORDER = theme.palette?.neutral?.greyRGBA50 ?? 'rgba(57, 59, 55, 0.5)';

export interface SwatchBackground {
  color?: string;
  image?: string;
}

/**
 * The trigger is a bare icon otherwise, which reads as decoration rather than as a
 * control. Padding plus a hover/pressed ground and a visible focus ring make it look
 * pressable — without touching how the dropdown itself behaves.
 */
const pressableTrigger = {
  '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    padding: 0,
    minHeight: 'auto',
    height: 'auto',
    margin: 0,
    '& fieldset': { border: 'none' },
    '&:before, &:after': { borderBottom: '0 !important' },
  },
  '& .MuiInput-underline:before, & .MuiInput-underline:after': {
    borderBottom: '0 !important',
  },
  '& .MuiInput-root:hover:not(.Mui-disabled):before': {
    borderBottom: '0 !important',
  },
  '& .MuiSelect-select, & .MuiSelect-selectMenu': {
    background: 'transparent',
    border: 'none',
    minHeight: 'auto',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: `${HEADER_CONTROL_OUTER_RADIUS}px`,
    transition: theme.palette?.propVars?.easeInOutTransition ?? 'all .3s ease-in-out',
    '&:hover': { backgroundColor: HOVER_GROUND },
    // MUI paints its own grey ground on focus; replace it so mouse and keyboard agree.
    '&:focus': { backgroundColor: PRESSED_GROUND },
    '&:focus-visible': {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: '1px',
    },
  },
  // MUI reserves room for the dropdown arrow with `&&&` specificity. The arrow is hidden,
  // so reclaim that space and pad evenly instead (repeated class beats MUI's own rule).
  '& .MuiSelect-select.MuiSelect-select.MuiSelect-select': {
    padding: `${HEADER_CONTROL_PADDING}px`,
  },
  '& .MuiSelect-icon': { display: 'none' },
};

export const themeSelectorStyles = {
  picker: {
    fontSize: theme.typography.fs14 as any,
    minWidth: 0,
    ...pressableTrigger,
  } as SxProps<MuiTheme>,

  pickerLarge: {
    minWidth: 0,
    ...pressableTrigger,
  } as SxProps<MuiTheme>,

  /**
   * A theme changes the background the client sees — the ball's colour is a separate
   * control — so the swatch shows that background with the theme's own object standing
   * on it. At this size the ground is what tells the options apart; an object alone
   * cannot, which is why Standard and Light used to look identical.
   */
  swatch: (background: SwatchBackground, width: number, height: number): SxProps<MuiTheme> => ({
    width: `${width}px`,
    height: `${height}px`,
    flex: 'none',
    boxSizing: 'border-box',
    borderRadius: `${HEADER_CONTROL_RADIUS}px`,
    border: `1px solid ${SWATCH_BORDER}`,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: background.color ?? 'transparent',
    ...(background.image
      ? {
          backgroundImage: `url(${background.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : {}),
    // Keeps the silver ball off the light ground, and reads as an object standing in a
    // scene rather than pasted onto it.
    '& .icon': {
      display: 'block',
      filter: `drop-shadow(0 ${Math.max(1, Math.round(height * 0.04))}px ${Math.max(
        1,
        Math.round(height * 0.05)
      )}px rgba(0, 0, 0, 0.32))`,
    },
  }),

  menuPaper: {
    '& .MuiMenu-list': {
      backgroundColor: 'white !important',
      padding: '6px',
      // Swatch-only rows: the previews say which theme is which, so no labels are needed.
      // Left-aligned rather than centred, so the tick on the active row cannot nudge its
      // swatch out of line with the others.
      '& .MuiMenuItem-root': {
        gap: '8px',
        borderRadius: '7px',
        padding: '8px 10px',
        minHeight: 'auto',
      },
      '& li:hover, & li:focus': {
        backgroundColor: '#f1f1f1',
      },
      // The dropdown is the practitioner's chance to verify before the client's screen
      // changes, so the active theme is ticked rather than only highlighted.
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: SELECTED_GROUND,
        '&:hover, &:focus': { backgroundColor: SELECTED_GROUND },
        '&:after': {
          content: '"\\2713"',
          marginLeft: 'auto',
          color: ACCENT,
          fontSize: '14px',
          lineHeight: 1,
        },
      },
    },
  } as SxProps<MuiTheme>,
};
